'use client';
import { useEffect, useState } from 'react';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { ProgressBar } from 'primereact/progressbar';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { exchangeGift } from '@/services/service';
import Image from 'next/image';

declare global {
  // interface Window {
  //   adsbygoogle: any[];
  // }
}

interface ExchangeResult {
  code: string;
  statusCode: number;
  status: string;
  retryCount?: number;
  processing?: boolean;
}

interface ResultsByGameId {
  [gameId: string]: ExchangeResult[];
}

interface Summary {
  id_count: number;
  code_count: number;
  total: number;
  processing: number;
  processed: number;
  success: number;
  failed: number;
  retry: number;
  expired: number;
  invalid: number;
  already: number;
}

export default function Home() {
  const [gameIds, setGameIds] = useState('');
  const [giftCodes, setGiftCodes] = useState('');
  const [results, setResults] = useState<ResultsByGameId>({});
  const [processing, setProcessing] = useState(false);
  const [summary, setSummary] = useState<Summary>({
    id_count: 0,
    code_count: 0,
    total: 0,
    processing: 0,
    processed: 0,
    success: 0,
    failed: 0,
    retry: 0,
    expired: 0,
    invalid: 0,
    already: 0
  });
  const spanStyle = "text-lg p-4 bg-purple-100 rounded-lg font-bold text-black";
  const [isAdBlocked, setIsAdBlocked] = useState(false);
  const getGiftCodes = async (): Promise<string> => {

    const listGiftCodes = [];
    listGiftCodes.push('cp666');
    listGiftCodes.push('cp888');
    // listGiftCodes.push('cp999'); // 2025.04.24 移除
    // listGiftCodes.push('CP666'); // 2025.04.24 移除
    listGiftCodes.push('CP888');
    // listGiftCodes.push('CP999'); // 2025.04.24 移除
    // listGiftCodes.push('LUCKY666'); // 2025.04.24 移除
    listGiftCodes.push('LUCKY888');
    // listGiftCodes.push('LUCKY999'); // 2025.04.24 移除
    listGiftCodes.push('LUCKY2025');
    listGiftCodes.push('lucky666');
    // listGiftCodes.push('lucky888'); // 2025.04.24 移除
    listGiftCodes.push('lucky999');
    listGiftCodes.push('lucky2025');
    // listGiftCodes.push('hinacapy25'); // 2025.03.21 移除
    // listGiftCodes.push('capyjingzhe'); // 2025.03.21 移除
    // 2025.03.21 新增
    // listGiftCodes.push('capychunfen'); // 2025.04.24 移除
    // 2025.04.09 新增 capyqingming 
    // listGiftCodes.push('capyqingming'); // 2025.04.24 移除
    // 2025.04.24 新增 capyeaster
    // listGiftCodes.push('capyeaster'); // 2025.04.24 移除
    // 2025.04.25 新增 capylabourday
    // listGiftCodes.push('capylabourday'); // 2025.06.19 移除
    // capylabourday, capypresent, Sorry4Delay, capydragonboat, RaineDropsKeepFalling, DannyShapedEggy, isHeDadOrMommy, UpdateWhen
    // 2025.06.19 新增
    // listGiftCodes.push('capypresent'); // 2025.06.19 移除
    // listGiftCodes.push('Sorry4Delay'); // 2025.06.19 移除
    // listGiftCodes.push('capydragonboat'); // 2025.06.19 移除
    listGiftCodes.push('RaineDropsKeepFalling');
    listGiftCodes.push('DannyShapedEggy');
    listGiftCodes.push('isHeDadOrMommy');
    listGiftCodes.push('UpdateWhen');





    const strGiftCodes = listGiftCodes.join(',');
    return strGiftCodes;
  };
  // 更新日期
  const updateDate = '2025.06.19';
  useEffect(() => {
    getGiftCodes().then(setGiftCodes);
  }, []);

  // 處理輸入的ID和禮包碼,去除重複
  const processInput = (input: string) => {
    // 如果不包含逗號，就用換行當作分隔符號辨識 並且排除空值
    if (!input.includes(',')) {
      return [...new Set(input.split('\n').map(x => x.trim()))].filter(x => x && x.trim() !== '');
    }
    return [...new Set(input.split(',').map(x => x.trim()))].filter(x => x && x.trim() !== '');
  };

  // 初始化結果表格
  const initializeResults = (ids: string[], codes: string[]) => {
    setSummary({
      id_count: ids.length,
      code_count: codes.length,
      total: ids.length * codes.length,
      processing: ids.length * codes.length,
      processed: 0,
      success: 0,
      failed: 0,
      retry: 0,
      expired: 0,
      invalid: 0,
      already: 0
    });
    const initialResults: ResultsByGameId = {};
    ids.forEach(id => {
      initialResults[id] = codes.map(code => ({
        code,
        statusCode: -1,
        status: '等待處理',
        processing: false
      }));
    });
    setResults(initialResults);
  };

  // 更新特定ID和Code的結果
  const updateResult = (id: string, code: string, update: Partial<ExchangeResult>) => {
    setResults(prev => {
      const newResults = {
        ...prev,
        [id]: prev[id].map(result =>
          result.code === code
            ? { ...result, ...update }
            : result
        )
      };

      // 使用最新的 results 計算統計數據
      const allResults = Object.values(newResults).flat();
      const processed = allResults.filter(result => !result.processing).length;
      const success = allResults.filter(result => result.statusCode === 0).length;
      const failed = allResults.filter(result => result.statusCode === -1).length;
      const retry = allResults.filter(result => result.statusCode === -2).length;
      const expired = allResults.filter(result => result.statusCode === 20403).length;
      const invalid = allResults.filter(result => result.statusCode === 20401).length;
      const already = allResults.filter(result => result.statusCode === 20407).length;

      // 立即更新 summary
      setSummary(prev => ({
        ...prev,
        processing: prev.total - processed,
        processed,
        success,
        failed,
        retry,
        expired,
        invalid,
        already
      }));

      return newResults;
    });
  };

  const handleExchange = async () => {

    if (!gameIds.trim() || !giftCodes.trim()) {
      return;
    }

    setProcessing(true);
    const ids = processInput(gameIds.replaceAll('，', ',').replaceAll(' ', ''));
    const codes = processInput(giftCodes.replaceAll('，', ',').replaceAll(' ', ''));

    // 初始化 summary
    setSummary({
      id_count: ids.length,
      code_count: codes.length,
      total: ids.length * codes.length,
      processing: ids.length * codes.length,
      processed: 0,
      success: 0,
      failed: 0,
      retry: 0,
      expired: 0,
      invalid: 0,
      already: 0
    });

    // 初始化結果表格
    initializeResults(ids, codes);

    try {
      // 建立所有兌換任務
      const exchangeTasks = ids.flatMap(id =>
        codes.map(code => ({
          id,
          code,
          task: async () => {
            let retryCount = 0;
            const maxRetries = 10;

            updateResult(id, code, { processing: true, status: '處理中...' });

            while (retryCount < maxRetries) {
              try {
                const result = await exchangeGift(id, code);

                // 20002 驗證碼錯誤
                // 20003 ID 無效
                // 20407 禮包碼已兌換
                // 20401 禮包碼無效
                // 20403 禮包碼已過期
                // 0 成功

                // 如果是 0 20003 20407 20401 20403 則直接更新結果
                if ([0, 20003, 20407, 20401, 20403].includes(result)) {
                  updateResult(id, code, {
                    statusCode: result,
                    status: statusMap[result],
                    processing: false,
                    retryCount: retryCount > 0 ? retryCount : undefined
                  });
                  return;
                }

                retryCount++;
                updateResult(id, code, {
                  statusCode: -2, // 重試中的狀態碼
                  status: `重試第 ${retryCount} 次`,
                  retryCount
                });

              } catch (error) {
                if (retryCount >= maxRetries - 1) {
                  updateResult(id, code, {
                    statusCode: -1, // 失敗的狀態碼
                    status: '兌換失敗',
                    processing: false,
                    retryCount
                  });
                  console.log('兌換失敗', id, code);
                  return;
                }
                retryCount++;
                updateResult(id, code, {
                  statusCode: -2, // 重試中的狀態碼
                  status: `重試第 ${retryCount} 次`,
                  retryCount
                });
              }

              // 加入小延遲避免請求太密集
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          }
        }))
      );

      // 並行執行所有任務
      await Promise.all(exchangeTasks.map(task => task.task()));

    } finally {
      setProcessing(false);
    }
  };

  // 狀態標籤渲染器
  // 成功: success, 沒辦法成功: danger, 重試: warning, 等待: info
  const statusTemplate = (result: ExchangeResult) => {
    let severity: 'success' | 'info' | 'warning' | 'danger' = 'info';
    if (result.statusCode === 0) severity = 'success';
    else if ([20003, 20401, 20403].includes(result.statusCode)) severity = 'danger';
    else if (result.statusCode === 20407) severity = 'warning';

    return (
      <Tag severity={severity} value={result.status} />
    );
  };
  // 對應狀態
  const statusMap: { [key: string]: string } = {
    20002: '驗證碼錯誤', // warning
    20003: 'ID 無效', // danger
    20407: '禮包碼已兌換',
    20401: '禮包碼無效',
    20403: '禮包碼已過期',
    0: '成功'
  };

  return (
    <main className="m-0 p-0">
      <div className="flex flex-col gap-4 max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold">批次兌換卡皮巴拉禮包</h1>

        <div className='flex-items'>
          <label className="block mb-2 font-bold">遊戲ID (用逗號或換行分隔)</label>
          {/* 範例 */}
          <div className="mb-2">
            <label className="block mb-2">範例:</label>
            <p className="text-sm text-gray-500">1234567,1234568,1234569</p>
          </div>
          <InputTextarea
            value={gameIds}
            onChange={(e) => setGameIds(e.target.value)}
            rows={5}
            className="w-full"
            placeholder="請輸入遊戲ID，多個ID請用逗號分隔"
            disabled={processing}
          />
        </div>

        <div>
          <div className='flex-items'>
            <label className="block mb-2 font-bold">禮包碼 (用逗號或換行分隔)</label>
            {/* 範例 */}
            <div className="mb-2">
              <p className="text-sm text-gray-500">更新至{updateDate} </p>
            </div>
            <Button
              label="帶入常見禮包碼"
              onClick={() => {
                getGiftCodes().then(setGiftCodes);
              }}
              disabled={processing}
              className="mb-2"
            />
            {/* 清空按鈕 */}
            <Button
              label="清空禮包碼"
              onClick={() => {
                setGiftCodes('');
              }}
              disabled={processing}
              className="mb-2"
            />
          </div>
          <InputTextarea
            value={giftCodes}
            onChange={(e) => setGiftCodes(e.target.value)}
            rows={5}
            className="w-full"
            placeholder="請輸入禮包碼，多個禮包碼請用逗號分隔"
            disabled={processing}
          />
        </div>

        <Button
          label="開始兌換"
          onClick={handleExchange}
          disabled={processing || !gameIds.trim() || !giftCodes.trim()}
          loading={processing}
        />
        {/* 進度條 */}
        {processing && summary && (
          <ProgressBar
            value={summary.total ? Number(((summary.processed / summary.total) * 100).toFixed(2)) : 0}
          />
        )}
        {/* Summary */}
        {summary && (
          <div className="flex-items gap-1">
            <label className={spanStyle}>ID 數: {summary.id_count}</label>
            <label className={spanStyle}>禮包碼數: {summary.code_count}</label>
            <label className={spanStyle + ""}>總任務數: {summary.total}</label>
            <label className={spanStyle + ""}>已處理: {summary.processed}</label>
            <label className={spanStyle}>處理中: {summary.processing}</label>
            <label className={spanStyle}>成功: {summary.success}</label>
            <label className={spanStyle}>失敗: {summary.failed}</label>
            <label className={spanStyle}>重試中: {summary.retry}</label>
            <label className={spanStyle}>禮包碼過期: {summary.expired}</label>
            <label className={spanStyle}>禮包碼無效: {summary.invalid}</label>
            <label className={spanStyle}>已兌換: {summary.already}</label>
          </div>
        )}
        {/* 取得ID教學 */}
        <div className="mt-4">
          <h2 className="text-lg font-bold mb-2">如何取得遊戲ID?</h2>
          <div className="space-y-4">
            <div className="space-y-2">
              <p>1. 開啟遊戲，點擊左上角的個人頭像</p>
              <Image
                src="/images/guide-1.png"
                alt="點擊個人頭像"
                width={0}
                height={0}
                sizes="100vw"
                className="mx-auto rounded-lg shadow-md w-full h-auto"
              />
            </div>
            <div className="space-y-2">
              <p>2. 在個人資料頁面中，您可以看到「UID」欄位</p>
              <Image
                src="/images/guide-2.png"
                alt="查看個人資料"
                width={0}
                height={0}
                sizes="100vw"
                className="mx-auto rounded-lg shadow-md w-full h-auto"
              />
            </div>
            <p>3. 複製數字部分即為您的遊戲ID</p>
          </div>
        </div>
        {/* 顯示兌換結果 */}
        {Object.entries(results).map(([gameId, resultsForId]) => (
          <div key={gameId} className="mb-4">
            <h2 className="text-lg font-bold mb-2">遊戲ID: {gameId}</h2>
            <DataTable value={resultsForId} className="w-full">
              <Column field="code" header="禮包代碼" />
              <Column field="status" header="兌換狀態" body={statusTemplate} />
            </DataTable>
          </div>
        ))}
      </div>
    </main>
  );
}
